import type { FunctionComponent, ReactElement, VoidFunctionComponent } from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import type { InjectedRouter } from 'react-router';
import { SidebarLayout } from '../layouts';
import PackageDetails from './PackageDetails';
import PackageInventoryFilters from './filters';
import PackageFilters from './filters/PackageFilters';
import useSyncPackagesStateAndURLParams from './hooks/useSyncPackagesStateAndURLParams';
import useSetFirstPackageActive from './hooks/useSetFirstPackageActive';
import PackageInventoryCTA from './PackageInventoryCTA';
import PackageInventorySidebar from './PackageInventorySidebar';
import * as PackageInventoryStore from '../../redux/packageInventory';
import useFetchPackages, { useRefetchPackagesOnTeamSelection } from './hooks/useFetchPackages';
import { PackageInventoryFiltersProjectPackagesContainer as ProjectFilters } from './filters/ProjectFilters';
import PaginationFooter from '../generic/Pagination/PaginationFooter';
import VulnFilters from './filters/VulnFilters';
import type { RouterProps } from '../../types';
import './PackageInventory.less';
import PackageIndexExportButton from './PackageIndexExportButton';

export type WithResetSelectedPackage = (fn: (...args: unknown[]) => void) => (...args: unknown[]) => void;

export type PackageInventoryParams = {
  packageLocator?: string;
  version?: string;
};

type PropsWithRouter = RouterProps<PackageInventoryParams> & {
  router: InjectedRouter;
};

/**
 * Component which is rendered when the user hits either:
 *   - /packages
 *   - /packages/:packageLocator
 * In the case of `/packages`, the useSetFirstPackageActive
 * hook will select the first package (when it exists) and
 * will bring the user to /packages/:packageLocator.
 *
 * `children` here comes from react-router, and in this case is PackageDetails.
 */
const PackageInventory: VoidFunctionComponent<PropsWithRouter> = ({ location, router, params }) => {
  const hasPackageErrors = useSelector(PackageInventoryStore.selectors.getHasPackageErrors);
  const hasFilterAndNoPackages = useSelector(PackageInventoryStore.selectors.getHasFilterAndNoPackages);

  const page = useSelector(PackageInventoryStore.selectors.getPackagesPage);
  const count = useSelector(PackageInventoryStore.selectors.getPackagesCount);
  const totalItems = useSelector(PackageInventoryStore.selectors.getTotalPackagesCount);
  const setPage = useFetchPackages(PackageInventoryStore.actions.setPackagesPage);
  const setCount = useFetchPackages(PackageInventoryStore.actions.setPackagesCount);

  const resetSelectedPageInURL = useCallback(() =>
    router.replace({
      pathname: `/packages`,
      search: location.search,
    }), [router, location.search]);

  //  When filters/search change, a new list of packages is fetched and the
  //  "active" package we show in the package details might change, so we clear it out.
  //  This will cause useSetFirstPackageActive to set the first package as active once the packages load.
  //  Note that this accepts a function (which dispatches an action for updating the state) and returns the final function
  const withResetSelectedPackage = useCallback((fn: (...args: unknown[]) => void) => (...args: unknown[]) => {
    fn(...args);
    resetSelectedPageInURL();
  }, [resetSelectedPageInURL]);

  //  Ensure that we reset the selected package when page or count change
  const onSetPage = withResetSelectedPackage(setPage);
  const onSetCount = withResetSelectedPackage(setCount);

  // Synchronize URL query parameters and Redux state.
  useSyncPackagesStateAndURLParams({ location, router });
  //  Set the first package as active if none exists.
  useSetFirstPackageActive({ location, router, params });
  //  Watch for changes to the TeamSelector in the CTA and refetch packages if it changes.
  useRefetchPackagesOnTeamSelection(resetSelectedPageInURL);

  return (
    <>
      <Helmet
        //  Renders `Package Inventory | FOSSA` if no selected package.
        title={params.packageLocator}
        titleTemplate="%s - Package Inventory | FOSSA"
        defaultTitle="Package Inventory | FOSSA"
      />
      <PackageInventoryCTA />
      <div className="container packageInventory" data-test-id="package-inventory-table">
        <SidebarLayout>
          <SidebarLayout.Main>
            <div className="package-inventory-controls">
              <PackageFilters withResetSelectedPackage={withResetSelectedPackage} />
              <PackageIndexExportButton />
            </div>
            <div className="package-inventory-container">
              {!hasPackageErrors && <PackageInventorySidebar withResetSelectedPackage={withResetSelectedPackage} />}
              <PackageDetails />
            </div>
            {!hasPackageErrors && !hasFilterAndNoPackages && (
              <PaginationFooter
                page={page}
                count={count}
                totalItems={totalItems}
                setPage={onSetPage}
                setCount={onSetCount}
              />
            )}
          </SidebarLayout.Main>
          {!hasPackageErrors && (
            <SidebarLayout.Sidebar>
              <PackageInventoryFilters>
                <ProjectFilters withResetSelectedPackage={withResetSelectedPackage} />
                <VulnFilters withResetSelectedPackage={withResetSelectedPackage} />
              </PackageInventoryFilters>
            </SidebarLayout.Sidebar>
          )}
        </SidebarLayout>
      </div>
    </>
  );
};

/**
 * Container component for the new package inventory.
 * As the root Route for `/packages`, its children is whatever
 * components are specified for the child <Route> components in
 * the routes.tsx file.
 *
 * For example, if the user is on `/packages/:packageLocator`,
 * this will render PackageInventory, whos `children` will be
 * PackageDetails. For `/packages/:packageLocator/:version`,
 * children is rendered directly (no <PackageInventory>),
 * meaning <PackageVersion> is rendered.
 */
const PackageInventoryContainer: FunctionComponent<PropsWithRouter> = ({ location, router, params, children }) => {
  //  If the version param exists, then there is no need for us to render the main UI.
  if (params.version) {
    return children as ReactElement;
  }

  return (
    <PackageInventory
      location={location}
      router={router}
      params={params}
    >
      {children}
    </PackageInventory>
  );
};

export default PackageInventoryContainer;
